import { Outlet, createRootRoute, redirect } from '@tanstack/react-router';

import { silentRefresh } from '@/shared/api/client';
import { fetchMe } from '@/shared/api/me';
import { useAuthStore } from '@/shared/auth/auth-store';

/**
 * Root route — auth gate for the admin app.
 *
 * Loads `/api/me` once per session; redirects to `/login` if:
 *   - No access token stored
 *   - /api/me returns 401
 *   - User is authenticated but `isSuperadmin !== true` (refuse politely)
 *
 * Public-by-list opt-out: `/login` itself bypasses the guard.  Anything not
 * in the public list goes through here.
 */
const PUBLIC_PATHS = new Set<string>(['/login']);

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (PUBLIC_PATHS.has(location.pathname)) return;

    const store = useAuthStore.getState();
    if (!store.accessToken) {
      // On reload the access token is gone (memory-only). Re-mint it from the
      // httpOnly refresh cookie before bouncing to /login.
      const restored = await silentRefresh();
      if (!restored) {
        throw redirect({ to: '/login' });
      }
    }

    try {
      const me = await fetchMe();
      if (!me.isSuperadmin) {
        // Authenticated user, but not an admin — refuse politely.  Drop tokens
        // so a re-load shows the login screen instead of a thrash loop.
        store.clear();
        throw redirect({ to: '/login', search: { reason: 'not_admin' } as never });
      }
      if (!store.isAuthenticated) {
        useAuthStore.getState().setAuthenticated(true);
      }
    } catch (err) {
      // Refresh-on-401 already attempted inside `api()`; if we're still throwing
      // here the session is definitely gone.
      if (err instanceof Error && err.name === 'ApiError') {
        store.clear();
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: () => <Outlet />,
});
